import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuditLogCategory, TOUR_PATH } from '@cdw-ae/common-data';
import { lookupCustomer } from '@cdw-ae/customer-lookup'; // Import the exported namespace directly
import { Store, select } from '@ngrx/store'; // Added select
import {
  catchError,
  filter,
  firstValueFrom,
  from,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import * as AppLifecyclesActions from '../../../+state/app-lifecycles/app-lifecycles.actions';
import { UserData } from '../../../+state/user-identity/user-identity-settings.models'; // Added model import
import { selectCurrentUserData } from '../../../+state/user-identity/user-identity-settings.selectors'; // Added selector import
import { LogoutReason } from '../../../app.tokens';
import { AuditLogBuilderService } from '../audit-log/audit-log-builder.service';
import { LifecyclesService } from '../lifecycle/lifecycle.service';
import { LoginVerdictStateService } from '../userIdentity/login-verdict-state.service';
import { UserIdentityService } from '../userIdentity/user-identity.service';
import { resolvePostAuthTarget } from './post-auth-target';

export enum LoginProvider {
  Google = 'google',
}

/**
 * EDUS-2657 — an inactivity timeout must be distinguishable from a deliberate
 * sign-out in the audit log, so the reason selects the wording rather than
 * every logout reading "User Logged Out".
 */
const LOGOUT_AUDIT_DETAIL: Record<
  LogoutReason,
  { operationType: string; action: string; description: string }
> = {
  'user-initiated': {
    operationType: 'Logout',
    action: 'User Logged Out',
    description: 'User logged out',
  },
  'inactivity-timeout': {
    operationType: 'Session Timeout',
    action: 'Session Timed Out',
    description: 'Session ended automatically after a period of inactivity',
  },
};

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private pendingRedirectUrl: string | null = null;

  constructor(
    private userIdentityService: UserIdentityService,
    private store: Store,
    private router: Router,
    private lifecyclesService: LifecyclesService,
    private route: ActivatedRoute,
    private auditLogBuilder: AuditLogBuilderService,
    private loginVerdictState: LoginVerdictStateService
  ) {
    // ─────────────────────────────────────────────────────────────────────
    // KNOWN DESIGN SMELL — this hook re-navigates on every `setUser`, not
    // only on a genuine sign-in.
    //
    // `LifecyclesService` keeps no once-only flag, and `setUser` is dispatched
    // again on Firebase's ~hourly silent token refresh. So the whole post-login
    // routing decision is recomputed, and acted on, long after login. Two
    // separate workarounds exist purely because of it:
    //
    //   1. `firstEntry` had to become a sticky latch on LoginVerdictStateService
    //      (EDUS-2852 / F4). `resolve-login` runs on every `onIdTokenChanged`,
    //      and the APP branch's forced token refresh re-fires it, so a correct
    //      `true` was being overwritten by an equally correct `false` from the
    //      second resolution. The latch protects it — and then has to be
    //      released below, because without a release a background refresh would
    //      re-divert a working user to /tour.
    //   2. `pendingRedirectUrl` is never cleared, so a background refresh can
    //      re-navigate to a redirect target captured at login, possibly an hour
    //      earlier. Known, deliberately NOT fixed here (out of scope).
    //
    // THE FIX WE SHOULD MAKE INSTEAD: gate this hook's navigation on a genuine
    // sign-in rather than on any `setUser`. `UserIdentityService` already draws
    // that distinction — it tracks `lastAuthenticatedUid` to tell a real
    // sign-in from a token refresh. Surfacing that (an explicit signal on the
    // resolution, or a once-per-session guard in LifecyclesService) and only
    // navigating when it is set would make BOTH workarounds unnecessary: the
    // latch could go, and the stale redirect could not fire. It was scoped out
    // of PR #1196 as an auth-flow restructure, not because it is wrong.
    // ─────────────────────────────────────────────────────────────────────
    this.lifecyclesService.addPostAuthHook(() => {
      const redirectUrl = this.pendingRedirectUrl;
      return from(
        (async () => {
          let currentUrl: string;
          if (!this.router.navigated) {
            currentUrl = await firstValueFrom(
              this.router.events.pipe(
                filter((e) => e instanceof NavigationEnd),
                take(1),
                map((e) => (e as NavigationEnd).urlAfterRedirects)
              )
            );
          } else {
            currentUrl = this.router.url;
          }
          return currentUrl;
        })()
      ).pipe(
        switchMap((currentUrl: string) => {
          const targetUrl = this.resolvePostAuthTarget(currentUrl, redirectUrl);
          return from(this.router.navigateByUrl(targetUrl)).pipe(
            tap((navigated) => {
              // First entry is a one-shot diversion: release the latch only
              // once the user is actually standing on the tour. This hook
              // re-runs on every setUser dispatch — including the hourly
              // background token refresh — and first entry outranks every
              // other rule, so a latch that never released would drag a
              // working user back to /tour an hour later. A failed navigation
              // (guard redirect, chunk load error) leaves it set so the next
              // resolution tries again. See firstEntryHandled().
              if (navigated && targetUrl === TOUR_PATH) {
                this.loginVerdictState.firstEntryHandled();
              }
            })
          );
        }),
        map(() => undefined)
      );
    });

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
        if (
          event.urlAfterRedirects !== '/welcome' &&
          !event.urlAfterRedirects.includes('/login')
        ) {
          sessionStorage.setItem('lastVisitedUrl', event.urlAfterRedirects);
        }
      });

    this.lifecyclesService.addPostLogoutHook(() =>
      from(this.router.navigate(['/welcome'])).pipe(map(() => undefined))
    );
  }

  /**
   * Where to land a user right after auth completes. The rules — and why they
   * are ordered the way they are — live with the function in
   * `post-auth-target.ts`; this only gathers the inputs.
   *
   * Routing it here rather than navigating after `reresolveAndRoute` avoids
   * racing this very hook, which fires off the setUser effect.
   */
  private resolvePostAuthTarget(
    currentUrl: string,
    redirectUrl: string | null
  ): string {
    return resolvePostAuthTarget({
      currentUrl,
      redirectUrl,
      // Recorded by UserIdentityService before it dispatches setUser, precisely
      // so it's readable here — see the ordering note in routeByVerdict.
      isFirstEntry: this.loginVerdictState.state()?.firstEntry === true,
    });
  }

  async getCurrentUrl(): Promise<string> {
    // Wait for the first NavigationEnd event if router not ready yet
    if (!this.router.navigated) {
      const event = await firstValueFrom(
        this.router.events.pipe(
          filter((e) => e instanceof NavigationEnd),
          take(1)
        )
      );
      return (event as NavigationEnd).urlAfterRedirects;
    } else {
      return this.router.url;
    }
  }

  async logout(reason: LogoutReason = 'user-initiated'): Promise<void> {
    // Create logout audit log before actually logging out
    try {
      const userData = await firstValueFrom(
        this.store.pipe(select(selectCurrentUserData), take(1))
      );

      if (userData?.claims?.core?.dbUserId) {
        // Deliberately not awaited. Signing out must never queue behind a
        // logging call: EDUS-2657 gates its redirect on this method
        // resolving, so awaiting a request that hangs would leave the user
        // sitting on authenticated data indefinitely — a `catchError` does
        // not help, because a hang never rejects.
        //
        // `keepalive: true` (EDUS-2657) so the write survives the hard
        // navigation `terminate$` fires once this method resolves — see
        // AuditLogBuilderService.createAuditLog.
        this.auditLogBuilder
          .createAuditLog(
            {
              userId: userData.claims.core.dbUserId,
              category: AuditLogCategory.AUTHENTICATION,
              ...LOGOUT_AUDIT_DETAIL[reason],
            },
            { keepalive: true }
          )
          .pipe(
            catchError((_error) => {
              // Failed to create logout audit log, but continue
              return of(null);
            })
          )
          .subscribe();
      }
    } catch (_error) {
      // Failed to create logout audit log, but continue
    }

    await firstValueFrom(this.lifecyclesService.executePreLogoutHooks());

    // Clear report state from sessionStorage
    sessionStorage.removeItem('aueView');

    this.store.dispatch(AppLifecyclesActions.initiateLogout());
    try {
      await firstValueFrom(this.userIdentityService.logout());
      await firstValueFrom(this.lifecyclesService.executePostLogoutHooks());
    } catch (_error: unknown) {
      // Logout failed, but we can continue
      // console.error('Logout error:', _error);
    }
  }

  async login(provider: LoginProvider): Promise<void> {
    await firstValueFrom(this.lifecyclesService.executePreAuthHooks());
    this.pendingRedirectUrl =
      this.route.snapshot.queryParamMap.get('redirectUrl') ||
      new URLSearchParams(window.location.search).get('redirectUrl');
    this.store.dispatch(AppLifecyclesActions.initiateAuth());

    switch (provider) {
      case LoginProvider.Google:
        try {
          await firstValueFrom(this.userIdentityService.loginWithGoogle());
          // Navigation is handled reactively:
          // setUser action → triggerPostLogin$ effect → postLogin() → post-auth hooks
          // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional — login errors handled by the reactive pipeline
        } catch (_error: unknown) {}
        break;

      default:
        return;
    }
  }

  private async _triggerCustomerLookup(): Promise<void> {
    await firstValueFrom(
      this.store.pipe(
        select(selectCurrentUserData),
        filter((userData): userData is UserData => !!userData?.email),
        take(1),
        tap((userData) => {
          // biome-ignore lint/style/noNonNullAssertion: email is guaranteed non-null by the filter above
          const domain = userData.email!.split('@')[1];
          if (domain) {
            this.store.dispatch(lookupCustomer({ domain }));
          }
        })
      )
    );
  }

  async postLogin(): Promise<void> {
    // console.log('POST LOGIN START');
    try {
      await this._triggerCustomerLookup();
    } catch (_error) {
      // Customer lookup failed: _error, but continue with post-auth hooks
      // console.error('Customer lookup failed:', _error);
    }

    // Execute existing post-auth hooks regardless of lookup success/failure
    await firstValueFrom(this.lifecyclesService.executePostAuthHooks());
  }

  async postLogout(): Promise<void> {
    await firstValueFrom(this.lifecyclesService.executePostLogoutHooks());
  }
}
